import type { ReactNode } from "react"
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { units } from "@/lib/format"
import type { NumberedAction } from "../lib/actions"
import { type Grain, salesMarks, salesSeries } from "../lib/salesSeries"
import type { SalesDay } from "../types"
import { ActionKey, MARKED_TOP, actionLines } from "./ActionMarks"

const AXIS_TICK = { fontSize: 10, fill: "var(--chart-axis)" }
const SOLD = "var(--series-1)"
const GIFTED = "var(--series-2)"

// How many points apart the date labels sit: every two weeks, every four
// weeks, every month.
const TICK_EVERY: Record<Grain, number> = { day: 14, week: 4, month: 1 }

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  )
}

// Units out in this market, sold (bottom) and gifted (stacked on top), the
// same sales the average-based stock line runs on, with the Push review
// actions marked on the days they ran. `controls` sit at the right end of
// the caption line.
export default function SalesGiftsChart({ days, grain, today, actions, controls }: { days: SalesDay[]; grain: Grain; today: string; actions: NumberedAction[]; controls?: ReactNode }) {
  const points = salesSeries(days, grain, today)
  const marks = salesMarks(days, grain, today, actions)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-foreground">Units sold or gifted, this market</p>
        {controls}
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={points} margin={{ top: marks.length > 0 ? MARKED_TOP : 16, right: grain === "month" ? 24 : 12, bottom: 0, left: 0 }} barCategoryGap={grain === "day" ? 1 : "20%"}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
          <XAxis
            dataKey="i"
            ticks={points.filter((p) => p.i % TICK_EVERY[grain] === 0).map((p) => p.i)}
            tickFormatter={(i: number) => points[i]?.tick ?? ""}
            interval={0}
            tick={AXIS_TICK}
          />
          <YAxis width={48} tick={AXIS_TICK} allowDecimals={false} tickFormatter={(value: number) => units(value)} />
          <Tooltip
            labelFormatter={(i) => points[Number(i)]?.title ?? ""}
            formatter={(value, name) => [units(Number(value)), name]}
            cursor={{ fill: "var(--muted)" }}
          />
          <Bar dataKey="sold" name="Sold" stackId="out" fill={SOLD} isAnimationActive={false} />
          <Bar dataKey="gifted" name="Gifted" stackId="out" fill={GIFTED} radius={[2, 2, 0, 0]} isAnimationActive={false} />
          {actionLines(marks)}
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-4 text-sm text-foreground">
        <LegendItem color={SOLD} label="Sold" />
        <LegendItem color={GIFTED} label="Gifted" />
      </div>
      <ActionKey marks={marks} today={today} />
    </div>
  )
}
